// src/components/DragAndDropFolderAndFileUpload/DragAndDropFolderAndFileUpload.jsx
// STORE DESTEKLİ TAM SÜRÜM (öncekini bununla değiştir)
// - Yeni props: sessionKey (opsiyonel) -> aynı anahtar ile yeniden açılınca kaldığı yerden devam eder
// - UploadStore kullanımı: enqueue/loop store tarafından yönetilir;
//   component sadece UI ve kullanıcı etkileşimi sağlar.

import React, {
  useRef, useState, useEffect, useImperativeHandle, forwardRef, useCallback
} from 'react';
import { useLanguage } from '../../LanguageProvider';
import './DragAndDropFolderAndFileUpload.css';
import { FiUploadCloud } from 'react-icons/fi';
import { TbCircleCheck, TbTrash, TbUpload } from 'react-icons/tb';
import { LuAlertTriangle, LuFolderPlus } from 'react-icons/lu';
import { getUploadStore } from '../../pages/Lodrive/UploadStore';

let queueIdForLocal = 0;

const DragAndDropFolderAndFileUpload = forwardRef(function DragAndDropFolderAndFileUpload(
  {
    accept,
    onUpload,
    onBusyChange,
    onBatchComplete,
    sessionKey,
    getDefaultTargetFolderId,
  },
  ref
) {
  const { getTranslation } = useLanguage();

  // ---- STORE entegrasyonu / fallback local state ----
  const isStoreMode = !!sessionKey;
  const storeRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Ekranda göstereceğimiz "anlık" state (store ya da local)
  const [uiItems, setUiItems] = useState([]);
  const [uiBusy, setUiBusy] = useState(false);
  const [uiStopRequested, setUiStopRequested] = useState(false);

  // Local moda düşersek (sessionKey yok), eski davranışa devam edelim
  const [localItems, setLocalItems] = useState([]);
  const [localBusy, setLocalBusy] = useState(false);
  const [localStopRequested, setLocalStopRequested] = useState(false);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const wrapperRef = useRef(null);

  const resolveTarget = useCallback((explicitTarget) => {
    if (explicitTarget != null) return explicitTarget; // grid içine bırakırken gelen id
    if (typeof getDefaultTargetFolderId === 'function') {
      try {
        const t = getDefaultTargetFolderId();
        if (t != null) return t;
      } catch { }
    }
    return null; // root’a düşsün (Lodrive tarafında ROOT_ID/null handle ediliyor)
  }, [getDefaultTargetFolderId]);


  // Store kurulumu
  useEffect(() => {
    if (!isStoreMode) return;

    const store = getUploadStore(sessionKey);
    storeRef.current = store;

    // onUpload fonksiyonunu store'a verelim
    store.setOnUpload(onUpload);
    store.setOnBatchComplete(onBatchComplete);
    // Abone ol: UI state'ini store'dan besle
    const unsub = store.subscribe((snap) => {
      setUiItems(snap.items);
      setUiBusy(snap.busy);
      setUiStopRequested(snap.stopRequested);
    });

    // İlk bildirim
    onBusyChange?.(store.busy);

    return () => {
      unsub?.();
      // unmount'ta store'u SİLME — kalıcılık için bırakıyoruz
    };
  }, [isStoreMode, sessionKey, onUpload, onBusyChange]);

  // Local mod için otomatik başlat/loop (önceki davranış)
  const localStartBatch = useCallback(async () => {
    if (!onUpload || localItems.length === 0) return;
    if (localBusy) return;

    setLocalStopRequested(false);
    setLocalBusy(true);
    try {
      for (const it of localItems) {
        if (localStopRequested) break;
        if (it.status !== 'pending') continue;

        setLocalItems(prev => prev.map(x => x.id === it.id ? { ...x, status: 'uploading' } : x));
        let ok = false;
        try {
          ok = await onUpload({ file: it.file, path: it.path, targetFolderId: it.targetFolderId });
        } catch {
          ok = false;
        }
        setLocalItems(prev => prev.map(x => x.id === it.id ? { ...x, status: ok ? 'success' : 'error' } : x));

        if (localStopRequested) break;
      }
    } finally {
      setLocalBusy(false);
      onBatchComplete?.();
    }
  }, [onUpload, onBatchComplete, localItems, localBusy, localStopRequested]);

  useEffect(() => {
    onBusyChange?.(isStoreMode ? uiBusy : localBusy);
  }, [uiBusy, localBusy, isStoreMode, onBusyChange]);

  // Local modda pending varsa auto-start
  useEffect(() => {
    if (!isStoreMode) {
      if (!localBusy && !localStopRequested && localItems.some(x => x.status === 'pending')) {
        Promise.resolve().then(() => localStartBatch());
      }
    }
  }, [isStoreMode, localItems, localBusy, localStopRequested, localStartBatch]);

  // ---- Ad kısaltma & yol formatlama ----
  const shortenName = (name) => {
    if (!name) return '';
    return name.length >= 9 ? (name.slice(0, 5) + '...') : name;
  };

  const formatDisplayPath = (fullPath) => {
    const norm = (fullPath || '').toString().replace(/\\/g, '/');
    const parts = norm.split('/').filter(Boolean);
    const file = parts.pop() || '';
    const shortFile = shortenName(file);
    const dirs = parts.map(shortenName);
    if (dirs.length >= 3) return `${dirs[0]}/…/${dirs[dirs.length - 1]}/${shortFile}`;
    if (dirs.length === 2) return `${dirs[0]}/${dirs[1]}/${shortFile}`;
    if (dirs.length === 1) return `${dirs[0]}/${shortFile}`;
    return shortFile;
  };

  // ---- DnD yardımcıları (aynı kaldı) ----
  const traverseFileTree = async (itemEntry) => {
    const files = [];
    const walk = async (entry, prefix = '') =>
      new Promise((resolve) => {
        if (entry.isFile) {
          entry.file(
            (file) => {
              Object.defineProperty(file, 'webkitRelativePath', {
                value: prefix ? `${prefix}/${file.name}` : file.name,
                writable: false,
              });
              files.push(file);
              resolve();
            },
            () => resolve()
          );
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const read = () => {
            reader.readEntries(async (entries) => {
              if (!entries || entries.length === 0) return resolve();
              for (const e of entries) {
                await walk(e, prefix ? `${prefix}/${entry.name}` : entry.name);
              }
              read();
            }, resolve);
          };
          read();
        } else {
          resolve();
        }
      });
    await walk(itemEntry);
    return files;
  };

  const collectDroppedFiles = async (dataTransfer) => {
    const { items, files } = dataTransfer;
    if (items && items.length) {
      const promises = [];
      for (const item of items) {
        const entry = item.getAsEntry?.() || item.webkitGetAsEntry?.() || null;
        if (entry) {
          promises.push(traverseFileTree(entry));
        } else {
          const f = item.getAsFile?.();
          if (f) promises.push(Promise.resolve([f]));
        }
      }
      const nested = (await Promise.all(promises)).flat();
      if (nested.length > 0) return nested;
    }
    return Array.from(files || []);
  };

  // ---- Kuyruğa ekleme ----
  const decideAndAddLocal = (toAdd) => {
    const isBatchFinished = (() => {
      if (localBusy) return false;
      if (localItems.length === 0) return true;
      return localItems.every(x => x.status === 'success' || x.status === 'error');
    })();

    if (isBatchFinished) {
      setLocalItems(toAdd);
    } else {
      setLocalItems(prev => [...prev, ...toAdd]);
    }
  };

  const enqueueLocal = (filesWithPaths, targetFolderId = null) => {
    const effectiveTarget = resolveTarget(targetFolderId); // NEW
    const toAdd = (filesWithPaths || []).map(fp => ({
      id: ++queueIdForLocal,
      file: fp.file,
      path: fp.path || fp.file?.webkitRelativePath || fp.file?.name,
      size: fp.file?.size ?? 0,
      status: 'pending',
      targetFolderId: effectiveTarget, // NEW: snapshot hedef
    }));
    decideAndAddLocal(toAdd);
  };

  const enqueueStore = (filesWithPaths, targetFolderId = null) => {
    const effectiveTarget = resolveTarget(targetFolderId); // NEW
    // store kendi item’ını oluşturuyor; target’ı oraya basacağız
    const s = storeRef.current;
    s?.enqueue(
      (filesWithPaths || []).map(fp => ({
        file: fp.file,
        path: fp.path || fp.file?.webkitRelativePath || fp.file?.name,
      })),
      effectiveTarget // NEW
    );
  };

  useImperativeHandle(ref, () => ({
    enqueue: (filesWithPaths, targetFolderId = null) => {
      if (!Array.isArray(filesWithPaths) || filesWithPaths.length === 0) return;
      isStoreMode ? enqueueStore(filesWithPaths, targetFolderId) : enqueueLocal(filesWithPaths, targetFolderId);
    },

    enqueueFromDataTransfer: async (dataTransfer, targetFolderId = null) => {
      const files = await collectDroppedFiles(dataTransfer);
      if (!files?.length) return;
      const filesWithPaths = files.map((file) => ({
        file,
        path: file.webkitRelativePath && file.webkitRelativePath.length > 0
          ? file.webkitRelativePath
          : file.path || file.name,
      }));
      // NEW: burada da snapshot hedefi uygula
      isStoreMode ? enqueueStore(filesWithPaths, targetFolderId) : enqueueLocal(filesWithPaths, targetFolderId);
    },

    stopGracefully: () => { /* aynı */ },
    resume: () => { /* aynı */ },
  }), [isStoreMode, enqueueLocal, enqueueStore]);
  // ---- DND ----
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  // ---- Inputs ----
  const triggerFile = () => fileInputRef.current?.click();
  const triggerFolder = () => folderInputRef.current?.click();

 const handleFileChange = (e) => {
    if (e.target.files?.length) {
      const arr = Array.from(e.target.files);
      const filesWithPaths = arr.map((file) => ({
        file,
        path: file.webkitRelativePath && file.webkitRelativePath.length > 0
          ? file.webkitRelativePath
          : file.path || file.name,
      }));
      // NEW: target yoksa o anki klasör snapshot’ı kullanılacak
      isStoreMode ? enqueueStore(filesWithPaths, null) : enqueueLocal(filesWithPaths, null);
      e.target.value = '';
    }
  };

  const handleFolderChange = (e) => {
    if (e.target.files?.length) {
      const arr = Array.from(e.target.files);
      const filesWithPaths = arr.map((file) => ({
        file,
        path: file.webkitRelativePath && file.webkitRelativePath.length > 0
          ? file.webkitRelativePath
          : file.path || file.name,
      }));
      // NEW: yine snapshot hedef
      isStoreMode ? enqueueStore(filesWithPaths, null) : enqueueLocal(filesWithPaths, null);
      e.target.value = '';
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = await collectDroppedFiles(e.dataTransfer);
    if (droppedFiles.length) {
      const filesWithPaths = droppedFiles.map((file) => ({
        file,
        path: file.webkitRelativePath && file.webkitRelativePath.length > 0
          ? file.webkitRelativePath
          : file.path || file.name,
      }));
      // NEW: content alanına bırakmada da snapshot hedef
      isStoreMode ? enqueueStore(filesWithPaths, null) : enqueueLocal(filesWithPaths, null);
    }
  };

  // ---- UI türevleri ----
  const items = isStoreMode ? uiItems : localItems;
  const busy = isStoreMode ? uiBusy : localBusy;
  const stopRequested = isStoreMode ? uiStopRequested : localStopRequested;

  const pendingCount = items.filter(x => x.status === 'pending').length;
  const uploadingCount = items.filter(x => x.status === 'uploading').length;
  const successCount = items.filter(x => x.status === 'success').length;
  const errorCount = items.filter(x => x.status === 'error').length;
  const completed = successCount + errorCount;
  const total = items.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  const controlState = (() => {
    if (busy && stopRequested) return 'stopping';
    if (busy && !stopRequested) return 'stop';
    if (!busy && stopRequested) return 'resume';
    return 'idle';
  })();

  const onControlClick = () => {
    if (controlState === 'stop') {
      isStoreMode ? storeRef.current?.stopGracefully() : (() => setLocalStopRequested(true))();
    } else if (controlState === 'resume') {
      isStoreMode ? storeRef.current?.resume() : (() => {
        setLocalStopRequested(false);
        if (!localBusy && localItems.some(x => x.status === 'pending')) {
          Promise.resolve().then(() => localStartBatch());
        }
      })();
    }
  };

  const controlLabel = (() => {
    if (controlState === 'stop') return getTranslation('stop') ?? 'Stop (Graceful)';
    if (controlState === 'stopping') return getTranslation('stopping') ?? 'Durduruluyor…';
    if (controlState === 'resume') return getTranslation('resume') ?? 'Devam Et';
    return getTranslation('resume') ?? 'Devam Et';
  })();

  const controlDisabled =
    (controlState === 'stopping') ||
    (controlState === 'idle') ||
    (controlState === 'resume' && pendingCount === 0);

  const containerClasses = [
    'dnd-ffu-container',
    dragActive ? 'dnd-ffu-active' : '',
    total > 0 ? 'has-items' : '',
  ].filter(Boolean).join(' ');

  const showProgress = total > 0 && (busy || (completed > 0 && completed < total));

  return (
    <div className="dnd-ffu-wrapper" style={{ height: '100%' }} ref={wrapperRef}>
      <div
        className={containerClasses}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        style={{ position: 'relative' }}
      >
        {/* Gizli inputlar */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="dnd-ffu-input"
          {...(accept ? { accept } : {})}
          onChange={handleFileChange}
        />
        <input
          ref={folderInputRef}
          type="file"
          className="dnd-ffu-input"
          webkitdirectory="true"
          directory="true"
          multiple
          onChange={handleFolderChange}
        />

        <div className="dnd-ffu-content">
          <span className="dnd-ffu-icon-holder">
            <FiUploadCloud size={24} className="dnd-ffu-icon" />
          </span>
          <p className="dnd-ffu-text">
            {busy
              ? (stopRequested
                ? (getTranslation('stoppingAfterCurrent') ?? 'Mevcut dosyadan sonra durduruluyor…')
                : (getTranslation('uploading') ?? 'Yükleniyor...'))
              : (getTranslation('dragAndDrop') ?? 'Tıklayın veya sürükleyin')}
          </p>
        </div>

        {/* HER ZAMAN GÖRÜNÜR SEÇİCİ BAR */}
        <div className="dnd-ffu-chooser-bar" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="dnd-ffu-chooser-btn"
            onClick={() => fileInputRef.current?.click()}
            title={getTranslation('selectFiles') ?? 'Dosya Seç'}
            disabled={busy && !stopRequested}
          >
            <TbUpload className="dnd-ffu-chooser-icon" />
            <span>{getTranslation('selectFiles') ?? 'Dosya Seç'}</span>
          </button>
          <button
            type="button"
            className="dnd-ffu-chooser-btn"
            onClick={() => folderInputRef.current?.click()}
            title={getTranslation('selectFolder') ?? 'Klasör Seç'}
            disabled={busy && !stopRequested}
          >
            <LuFolderPlus className="dnd-ffu-chooser-icon" />
            <span>{getTranslation('selectFolder') ?? 'Klasör Seç'}</span>
          </button>
        </div>
      </div>

      {/* Liste */}
      {total > 0 && (
        <div className="dnd-ffu-list-wrap">
          <div className="dnd-ffu-list-toolbar">
            <div className="dnd-ffu-toolbar-actions">
              {/* Start/Resume */}
              <button
                className="dnd-ffu-footer-btn"
                onClick={onControlClick}
                disabled={controlDisabled}
                title={controlLabel}
              >
                {controlLabel}
              </button>

              <button
                className="dnd-ffu-footer-btn"
                onClick={() => (isStoreMode ? storeRef.current?.clearFinished() : (
                  setLocalItems(prev => prev.filter(x => x.status === 'pending' || x.status === 'uploading'))
                ))}
                disabled={busy || (successCount + errorCount) === 0}
              >
                {getTranslation('clearFinished') ?? 'Bitenleri Temizle'}
              </button>

              <button
                className="dnd-ffu-footer-btn"
                onClick={() => (isStoreMode ? storeRef.current?.clearAll() : setLocalItems([]))}
                disabled={busy || total === 0}
              >
                <TbTrash /> {getTranslation('deleteAll') ?? 'Hepsini Sil'}
              </button>
            </div>
          </div>

          <ul className="dnd-ffu-upload-list">
            {items.map((u) => (
              <li key={u.id} className="dnd-ffu-upload-item">
                <div className="dnd-ffu-file-info">
                  <div className="dnd-ffu-row">
                    <div className="dnd-ffu-left">
                      <span className="dnd-ffu-icon-holder is-file">
                        <TbUpload size={18} className="dnd-ffu-icon" />
                      </span>
                      <div className="dnd-ffu-name-col">
                        <span className="dnd-ffu-file-name" title={u.path || u.file.name}>
                          {formatDisplayPath(u.path || u.file.name)}
                        </span>
                        <span className="dnd-ffu-file-size">{(u.size / 1024).toFixed(1)} KB</span>
                      </div>
                    </div>

                    <div className="dnd-ffu-actions">
                      {u.status === 'success' && <TbCircleCheck className="dnd-ffu-action-btn success" title={getTranslation('success') ?? 'Başarılı'} />}
                      {u.status === 'error' && <span className="dnd-ffu-fail" title={getTranslation('failed') ?? 'Hatalı'}>✕</span>}
                      {u.status === 'uploading' && <div className="dnd-ffu-spinner" title={getTranslation('uploading') ?? 'Yükleniyor...'} />}
                      {u.status === 'pending' && !busy && (
                        <TbTrash
                          className="dnd-ffu-action-btn"
                          title={getTranslation('delete') ?? 'Sil'}
                          onClick={() => {
                            if (isStoreMode) {
                              const s = storeRef.current;
                              s.items = s.items.filter(x => x.id !== u.id);
                              s.notify();
                            } else {
                              setLocalItems(prev => prev.filter(x => x.id !== u.id));
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Alt progress bar */}
          {showProgress && (
            <div className="dnd-ffu-progress-footer" role="status" aria-live="polite">
              <div className="dnd-ffu-progress-text">
                {completed}/{total} • {percent}%
                {stopRequested && ' • ' + (getTranslation('stopped') ?? 'Mevcut dosyadan sonra duracak')}
              </div>
              <div className="dnd-ffu-progress-track" aria-hidden>
                <div className="dnd-ffu-progress-fill" style={{ width: `${percent}%` }} />
              </div>
              <div className="dnd-ffu-warning">
                <span className="dnd-ffu-warning-icon">
                  <LuAlertTriangle size={16} />
                </span>
                <span>
                  {getTranslation('doNotCloseWhileUploading')}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default DragAndDropFolderAndFileUpload;